import { ANNUAL_DISCOUNT_PERCENT } from 'definitions/billing-plans';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useAddClient } from '@/state/useAddClient';
import { PillTabs } from '../ConsolePanel';
import { PlanCards } from '../Settings/Billing/PlanCards';
import { PlanSummary } from '../Settings/Billing/PlanSummary';

const FIELD_INPUT = 'border-line bg-field text-foreground h-10 rounded-lg text-body';

const INTERVAL_OPTIONS = [
  { label: 'Monthly', value: 'month' },
  { label: `Yearly · save ${ANNUAL_DISCOUNT_PERCENT}%`, value: 'year' },
] as const;

/** On a plan that is only recorded nothing is paid, so there is no saving to advertise. */
const LABEL_INTERVAL_OPTIONS = [
  { label: 'Monthly', value: 'month' },
  { label: 'Yearly', value: 'year' },
] as const;

const BILLING_OPTIONS = [
  { label: 'Card', value: 'card' },
  { label: 'Invoice', value: 'invoice' },
] as const;

type AddClientDialogProps = { open: boolean; onClose: () => void };

/**
 * Adds a client with its website and its plan, in two steps: the details, then the plan with a
 * summary of exactly what accepting does. Nothing is created until the last step is accepted.
 * On a card account what follows is Polar's checkout for that client's plan; on an agency billed
 * by invoice or free the plan is only recorded. Each client has a plan of its own, billed on the
 * agency's account, so it is chosen here and not left for later.
 */
export const AddClientDialog = ({ open, onClose }: AddClientDialogProps) => {
  const add = useAddClient({ onDone: onClose });

  return (
    <Dialog open={open} onOpenChange={(next) => !next && add.cancel()}>
      <DialogContent className={add.step === 'plan' ? 'max-w-4xl' : 'max-w-lg'}>
        <DialogHeader>
          <DialogTitle>{add.step === 'plan' ? `Choose a plan for ${add.name.trim()}` : 'Add a client'}</DialogTitle>
          <DialogDescription>
            {add.step === 'plan'
              ? add.offCard
                ? 'Each client has its own plan, which its sessions are measured against. Nothing is created until you accept.'
                : 'Each client has its own plan, billed to your account. Nothing is created until you accept.'
              : add.offCard
                ? 'Its name and website now, and its plan next. Nothing is charged by card.'
                : 'Its name and website now, and its plan next. Your own website is included in the agency fee; each client is billed as its own plan.'}
          </DialogDescription>
        </DialogHeader>

        {add.step === 'details' ? (
          <form
            className="grid gap-5"
            onSubmit={(ev) => {
              ev.preventDefault();
              add.continueFromDetails();
            }}
          >
            <div className="grid gap-2">
              <label htmlFor="new-client-name" className="micro-label block">
                Client name
              </label>
              <Input
                id="new-client-name"
                value={add.name}
                onChange={(ev) => add.setName(ev.target.value)}
                placeholder="Enter client name"
                className={FIELD_INPUT}
                autoFocus
              />
            </div>
            <div className="grid gap-2">
              <label htmlFor="new-client-website" className="micro-label block">
                Website address
              </label>
              <Input
                id="new-client-website"
                value={add.url}
                onChange={(ev) => add.setUrl(ev.target.value)}
                placeholder="https://example.com"
                inputMode="url"
                className={FIELD_INPUT}
                aria-invalid={add.isUrlInvalid}
              />
              <span className={add.isUrlInvalid ? 'text-danger text-nano' : 'text-dim text-nano'}>
                {add.isUrlInvalid
                  ? 'That does not look like a website address.'
                  : 'The homepage, e.g. https://yourclient.com. Add more websites later.'}
              </span>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                className="border-line rounded-lg bg-transparent"
                onClick={add.cancel}
              >
                Cancel
              </Button>
              <Button type="submit" className="rounded-lg" disabled={!add.canContinue}>
                {add.isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Choose a plan
              </Button>
            </div>
          </form>
        ) : (
          <div className="flex flex-col gap-4">
            {add.canInvoice && (
              <div className="flex flex-col gap-[7px]">
                <span className="micro-label">Billed by</span>
                <div className="whitespace-nowrap">
                  <PillTabs
                    options={BILLING_OPTIONS}
                    value={add.billing}
                    onChange={(value) => add.setBilling(value === 'invoice' ? 'invoice' : 'card')}
                  />
                </div>
              </div>
            )}
            <div className="flex flex-col gap-[7px]">
              <span className="micro-label">Billing</span>
              <div className="whitespace-nowrap">
                <PillTabs
                  options={add.labelOnly ? LABEL_INTERVAL_OPTIONS : INTERVAL_OPTIONS}
                  value={add.interval}
                  onChange={(value) => add.setInterval(value === 'month' ? 'month' : 'year')}
                />
              </div>
            </div>

            <PlanCards selected={add.picked} interval={add.interval} onSelect={add.pick} />

            {add.summary && (
              <PlanSummary
                label="Your choice"
                title={add.summary.title}
                line={add.summary.line}
                rows={add.summary.rows}
                ctaLabel={
                  add.isSaving
                    ? 'Adding the client...'
                    : add.labelOnly
                      ? add.summary.ctaLabel
                      : `${add.summary.ctaLabel} and add ${add.name.trim()}`
                }
                note={add.summary.note}
                showPaymentMethods={!add.labelOnly && add.checkoutAvailable}
                disabled={!add.canAccept}
                isPending={add.isSaving}
                onContinue={add.accept}
                secondary={{ label: 'Choose another plan', onClick: add.clearPick }}
              />
            )}

            {!add.summary && (
              <div className="flex justify-start">
                <Button
                  type="button"
                  variant="outline"
                  className="border-line rounded-lg bg-transparent"
                  onClick={add.back}
                >
                  Back
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
